import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import api from "../../api";
import { message, Row, Col, Pagination, Spin, Image, Button } from "antd";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { Link } from "react-router-dom";
import { useMealMate } from "../../context/MealMateContext";

// Custom hook for debounced value
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Hàm xử lý tìm kiếm tiếng Việt
const createSearchPattern = (term) => {
  if (!term || typeof term !== "string") return ".*"; // Trả về regex khớp tất cả nếu term không hợp lệ
  const noAccents = removeAccents(term.trim());

  // Escape ký tự đặc biệt trong regex
  const escapeRegex = (str) => str.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");

  const base = `(${escapeRegex(term)}|${escapeRegex(noAccents)})`;

  return base
    .replace(/o/g, "[oóòỏõọôốồổỗộơớờởỡợ0]")
    .replace(/a/g, "[aáàảãạâấầẩẫậăắằẳẵặ]")
    .replace(/e/g, "[eéèẻẽẹêếềểễệ]")
    .replace(/u/g, "[uúùủũụưứừửữự]")
    .replace(/i/g, "[iíìỉĩị]")
    .replace(/y/g, "[yýỳỷỹỵ]")
    .replace(/d/g, "[dđ]");
};

const removeAccents = (str) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
};

// Styled Components
const RecipeListPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const FilterSidebar = styled.aside`
  width: 280px;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 2rem;

  @media (max-width: 768px) {
    width: 100%;
    position: relative;
    top: 0;
    margin-bottom: 1rem;
  }
`;

const FilterSection = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterTitle = styled.h3`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #666;
  font-size: 0.9rem;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  &:hover {
    color: #333;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const RangeInputGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const RangeInput = styled.input`
  width: 80px;
  padding: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: rgb(255, 159, 28);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FilterButton = styled(Button)`
  width: 100%;
  padding: 0.8rem;
  background: rgb(255, 159, 28);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  &:hover {
    background: rgb(255, 174, 66);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ResetButton = styled(Button)`
  width: 100%;
  padding: 0.8rem;
  background: #fff;
  color: rgb(255, 159, 28);
  border: 1px solid rgb(255, 159, 28);
  margin-top: 1rem;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  &:hover {
    background: rgba(255, 159, 28, 0.1);
  }
`;

const ContentArea = styled.div`
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }

  h1 {
    font-size: 2.2rem;
    color: #333;

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }
  }
`;

const SearchInput = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  width: 300px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: rgb(255, 159, 28);
    box-shadow: 0 0 5px rgba(255, 159, 28, 0.3);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const RecipeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  padding-top: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RecipeCardWrapper = styled(Link)`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    height: auto;
  }
`;

const FavoriteButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: 1.5rem;
  color: ${(props) => (props.isFavorite ? "rgb(255, 159, 28)" : "#a0a0a0")};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, background 0.2s ease;
  z-index: 10;

  &:hover {
    transform: scale(1.1);
    background: rgba(255, 159, 28, 0.1);
  }

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1.3rem;
  }
`;

const RecipeImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
  background-color: #f5f5f5;

  @media (max-width: 768px) {
    height: 150px;
  }
`;

const RecipeImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${RecipeCardWrapper}:hover & {
    transform: scale(1.05);
  }
`;

const RecipeContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const RecipeName = styled.div`
  font-size: 1.4rem;
  color: #333;
  margin-bottom: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;

  &:hover {
    color: rgb(255, 159, 28);
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
  color: #666;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    flex-wrap: nowrap;
    gap: 0.5rem;
    justify-content: center;
    font-size: 0.85rem;
  }
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const RecipeDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin: 0.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Tag = styled.span`
  background: rgba(255, 159, 28, 0.1);
  color: rgb(255, 159, 28);
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;

  &:hover {
    background: rgba(255, 159, 28, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.2rem 0.6rem;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  color: #777;
  font-size: 1.1rem;
  text-align: center;
  grid-column: 1 / -1;

  @media (max-width: 768px) {
    padding: 2rem 0;
    font-size: 1rem;
  }
`;

const EmptyStateIcon = styled.span`
  font-size: 4rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const EmptyStateText = styled.p`
  max-width: 400px;
  line-height: 1.5;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  grid-column: 1 / -1;
`;

const ErrorMessage = styled.div`
  background-color: rgba(255, 159, 28, 0.1);
  border: 1px solid rgba(255, 159, 28, 0.5);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  color: rgb(255, 159, 28);
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.5rem;
  }
`;

const PlaceholderCard = styled.div`
  visibility: hidden;
  background-color: transparent;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  height: 380px;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  margin-bottom: 2rem;

  .ant-pagination {
    .ant-pagination-item {
      border-radius: 6px;

      &-active {
        background-color: rgb(255, 159, 28);
        border-color: rgb(255, 159, 28);

        a {
          color: white;
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      button {
        border-radius: 6px;
        color: rgb(255, 159, 28);

        &:hover {
          background: rgba(255, 159, 28, 0.1);
        }
      }
    }
  }

  @media (max-width: 768px) {
    margin: 1rem 0;
  }
`;

// Component
const RecipePage = () => {
  const [allRecipes, setAllRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recipesPerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  const [filters, setFilters] = useState({
    calories: { min: "", max: "" },
    category: "",
    sortBy: "newest",
  });

  const { isAuthenticated, triggerFavoriteRecipesRefresh, setShowLoginModal } =
    useMealMate();

  const fetchRecipes = useCallback(async () => {
    setLoadingRecipes(true);
    setError(null);
    try {
      let favoriteIds = [];
      if (isAuthenticated) {
        try {
          const favoriteResponse = await api.get("/recipes/favorites");
          if (
            favoriteResponse.data &&
            Array.isArray(favoriteResponse.data.data)
          ) {
            favoriteIds = favoriteResponse.data.data;
          }
        } catch (error) {
          console.error("Lỗi khi lấy danh sách yêu thích:", error);
        }
      }

      const queryParams = new URLSearchParams();

      if (filters.calories.min) {
        queryParams.append("minCalories", filters.calories.min);
      }
      if (filters.calories.max) {
        queryParams.append("maxCalories", filters.calories.max);
      }
      if (filters.category) {
        queryParams.append("category", filters.category);
      }

      let sort = "";
      switch (filters.sortBy) {
        case "oldest":
          sort = "createdAt";
          break;
        case "newest":
          sort = "-createdAt";
          break;
        case "calories-high":
          sort = "-calories";
          break;
        case "calories-low":
          sort = "calories";
          break;
        default:
          sort = "-createdAt";
      }
      queryParams.append("sort", sort);

      const searchQuery = queryParams.toString()
        ? `?${queryParams.toString()}`
        : "";
      const response = await api.get(`/recipes${searchQuery}`);

      if (response.data && response.data.data) {
        const recipesWithFavorites = response.data.data.map((recipe) => ({
          ...recipe,
          isFavorite: favoriteIds.includes(recipe._id),
        }));
        setAllRecipes(recipesWithFavorites);
      } else {
        setAllRecipes([]);
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Không thể tải danh sách món ăn";
      setError(errorMsg);
      setAllRecipes([]);
      message.error(errorMsg);
    } finally {
      setLoadingRecipes(false);
    }
  }, [filters, isAuthenticated]);

  // Lọc tìm kiếm phía frontend với hỗ trợ tiếng Việt
  useEffect(() => {
    if (!debouncedSearchTerm.trim()) {
      setFilteredRecipes(allRecipes);
      return;
    }

    const searchPattern = createSearchPattern(debouncedSearchTerm);
    const regex = new RegExp(searchPattern, "i");

    const filtered = allRecipes.filter(
      (recipe) =>
        regex.test(recipe.name) ||
        (recipe.description && regex.test(recipe.description)) ||
        (recipe.category && regex.test(recipe.category)) ||
        (recipe.tags && recipe.tags.some((tag) => regex.test(tag)))
    );
    setFilteredRecipes(filtered);
    setCurrentPage(1); // Reset về trang 1 khi tìm kiếm
  }, [allRecipes, debouncedSearchTerm]);

  // Phân trang phía frontend
  useEffect(() => {
    const startIndex = (currentPage - 1) * recipesPerPage;
    const endIndex = startIndex + recipesPerPage;
    setRecipes(filteredRecipes.slice(startIndex, endIndex));
  }, [filteredRecipes, currentPage, recipesPerPage]);

  const handleToggleFavorite = useCallback(
    async (e, recipeId, currentIsFavorite) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isAuthenticated) {
        message.warning("Bạn cần đăng nhập để thêm vào yêu thích.");
        setShowLoginModal(true);
        return;
      }

      setAllRecipes((prev) =>
        prev.map((r) =>
          r._id === recipeId ? { ...r, isFavorite: !currentIsFavorite } : r
        )
      );

      try {
        if (currentIsFavorite) {
          await api.delete(`/recipes/favorites/${recipeId}`);
          message.success("Đã bỏ yêu thích.");
        } else {
          await api.post("/recipes/favorites", { recipeId });
          message.success("Đã thêm vào yêu thích.");
        }
        triggerFavoriteRecipesRefresh();
      } catch (error) {
        setAllRecipes((prev) =>
          prev.map((r) =>
            r._id === recipeId ? { ...r, isFavorite: currentIsFavorite } : r
          )
        );
        message.error(
          error.response?.data?.message || "Không thể cập nhật yêu thích"
        );
      }
    },
    [isAuthenticated, setShowLoginModal, triggerFavoriteRecipesRefresh]
  );

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const handleResetFilters = () => {
    setFilters({
      calories: { min: "", max: "" },
      category: "",
      sortBy: "newest",
    });
    setSearchTerm("");
    setCurrentPage(1);
    fetchRecipes();
  };

  return (
    <RecipeListPageWrapper>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <FilterSidebar>
            <FilterSection>
              <FilterTitle>Sắp xếp theo</FilterTitle>
              <FilterGroup>
                {[
                  { value: "newest", label: "Mới nhất" },
                  { value: "oldest", label: "Cũ nhất" },
                  { value: "calories-high", label: "Calories cao → thấp" },
                  { value: "calories-low", label: "Calories thấp → cao" },
                ].map(({ value, label }) => (
                  <CheckboxLabel key={value}>
                    <input
                      type="radio"
                      name="sortBy"
                      value={value}
                      checked={filters.sortBy === value}
                      onChange={(e) =>
                        setFilters({ ...filters, sortBy: e.target.value })
                      }
                    />
                    {label}
                  </CheckboxLabel>
                ))}
              </FilterGroup>
            </FilterSection>

            <FilterSection>
              <FilterTitle>Lọc theo calories</FilterTitle>
              <FilterGroup>
                <RangeInputGroup>
                  <RangeInput
                    type="number"
                    placeholder="Min"
                    value={filters.calories.min}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value < 0) return;
                      setFilters({
                        ...filters,
                        calories: { ...filters.calories, min: value },
                      });
                    }}
                  />
                  <span>-</span>
                  <RangeInput
                    type="number"
                    placeholder="Max"
                    value={filters.calories.max}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value < 0) return;
                      setFilters({
                        ...filters,
                        calories: { ...filters.calories, max: value },
                      });
                    }}
                  />
                </RangeInputGroup>
              </FilterGroup>
            </FilterSection>

            <FilterSection>
              <FilterTitle>Loại món ăn</FilterTitle>
              <FilterGroup>
                {["Bữa sáng", "Bữa trưa", "Bữa tối", "Tráng miệng"].map(
                  (cat) => (
                    <CheckboxLabel key={cat}>
                      <input
                        type="radio"
                        name="category"
                        checked={filters.category === cat}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            category: e.target.checked ? cat : "",
                          })
                        }
                      />
                      {cat}
                    </CheckboxLabel>
                  )
                )}
              </FilterGroup>
            </FilterSection>

            <FilterButton
              onClick={() => {
                setCurrentPage(1);
                fetchRecipes();
              }}
            >
              Áp dụng bộ lọc
            </FilterButton>
            <ResetButton onClick={handleResetFilters}>Đặt lại</ResetButton>
          </FilterSidebar>
        </Col>

        <Col xs={24} md={18}>
          <ContentArea>
            <Header>
              <h1>Khám phá món ăn</h1>
              <SearchInput
                type="text"
                placeholder="Tìm kiếm món ăn..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Header>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {loadingRecipes ? (
              <LoadingWrapper>
                <Spin size="large" />
              </LoadingWrapper>
            ) : (
              <>
                <RecipeGrid>
                  {recipes.length > 0 ? (
                    <>
                      {recipes.map((recipe) => (
                        <RecipeCardWrapper
                          key={recipe._id}
                          to={`/recipes/${recipe.slug}`}
                        >
                          <RecipeImageWrapper>
                            <RecipeImage
                              src={
                                recipe.imageUrl ||
                                "https://placehold.co/280x200?text=No+Image"
                              }
                              alt={recipe.name || "Recipe"}
                              preview={false}
                            />
                            {isAuthenticated && (
                              <FavoriteButton
                                isFavorite={recipe.isFavorite}
                                onClick={(e) =>
                                  handleToggleFavorite(
                                    e,
                                    recipe._id,
                                    recipe.isFavorite
                                  )
                                }
                                title={
                                  recipe.isFavorite
                                    ? "Bỏ khỏi yêu thích"
                                    : "Thêm vào yêu thích"
                                }
                              >
                                {recipe.isFavorite ? (
                                  <AiFillHeart />
                                ) : (
                                  <AiOutlineHeart />
                                )}
                              </FavoriteButton>
                            )}
                          </RecipeImageWrapper>

                          <RecipeContent>
                            <RecipeName>
                              {recipe.name || "Tên món ăn"}
                            </RecipeName>
                            <StatsRow>
                              <StatItem>
                                👁 {recipe.views || 0} lượt xem
                              </StatItem>
                              <StatItem>
                                🔥 {Math.round(recipe.calories || 0)} kcal
                              </StatItem>
                            </StatsRow>
                            <RecipeDescription>
                              {recipe.description ||
                                "Chưa có mô tả cho món ăn này"}
                            </RecipeDescription>
                            {recipe.tags && recipe.tags.length > 0 && (
                              <TagsContainer>
                                {recipe.tags.map((tag, index) => (
                                  <Tag key={index}>#{tag}</Tag>
                                ))}
                              </TagsContainer>
                            )}
                          </RecipeContent>
                        </RecipeCardWrapper>
                      ))}
                      {Array.from({
                        length: recipesPerPage - recipes.length,
                      }).map((_, index) => (
                        <PlaceholderCard key={`placeholder-${index}`} />
                      ))}
                    </>
                  ) : (
                    <EmptyState>
                      <EmptyStateIcon>
                        {searchTerm ? "🔍" : "🍽️"}
                      </EmptyStateIcon>
                      <EmptyStateText>
                        {searchTerm
                          ? "Không tìm thấy món ăn nào phù hợp với từ khóa của bạn."
                          : "Chưa có món ăn nào được thêm."}
                        {searchTerm && (
                          <Button
                            onClick={handleResetFilters}
                            style={{ marginTop: "1rem" }}
                          >
                            Xóa tìm kiếm
                          </Button>
                        )}
                      </EmptyStateText>
                    </EmptyState>
                  )}
                </RecipeGrid>

                {filteredRecipes.length > 0 && (
                  <PaginationWrapper>
                    <Pagination
                      current={currentPage}
                      total={filteredRecipes.length}
                      pageSize={recipesPerPage}
                      onChange={(page) => {
                        setCurrentPage(page);
                        window.scrollTo(0, 0);
                      }}
                      showSizeChanger={false}
                      showQuickJumper
                      showTotal={(total, range) =>
                        `${range[0]}-${range[1]} của ${total} món ăn`
                      }
                    />
                  </PaginationWrapper>
                )}
              </>
            )}
          </ContentArea>
        </Col>
      </Row>
    </RecipeListPageWrapper>
  );
};

export default RecipePage;
